import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  colorForC2M,
  rgbAtPercent,
  C2M_CANVAS,
  C2M_LEGEND,
  C2M_ARROW,
  drawC2MLegend,
  drawScanlineArrow,
} from '../utils/c2mRender';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { useAnalysisActions } from '../contexts/AnalysisActionsContext';
import { getClientById, getObjectById, getInspectionById } from '../utils/storage';
import { AngleInput, ProgressModal } from '../utils/defoCommon';
import { bundleShellResults } from '../utils/defoExport';
import { post } from '../api';
import './ShellDeformationPage.css';

// ── Chart helpers ────────────────────────────────────────────────────
// Tiny SVG chart utilities — kept inline to avoid pulling in a chart
// library. The plots are intentionally austere: single polyline, major
// ticks only, axis labels, no point markers.

function niceTicks(min, max, target = 5) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return Number.isFinite(min) ? [min] : [];
  }
  const range = max - min;
  const rough = range / Math.max(1, target - 1);
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / pow;
  let step;
  if (norm < 1.5) step = 1 * pow;
  else if (norm < 3) step = 2 * pow;
  else if (norm < 7) step = 5 * pow;
  else step = 10 * pow;
  const start = Math.ceil(min / step) * step;
  const ticks = [];
  for (let v = start; v <= max + step * 1e-6; v += step) ticks.push(v);
  return ticks;
}

// Fixed export-resolution canvas: 3306 × 1800 px. The legend overlays
// the rightmost 4 % of the canvas (gradient bar + filled line-graph
// histogram + numeric scale). The tank is sized against the FULL canvas
// (not a sub-frame), so the image width is not extended by the legend;
// the legend simply sits on top of the canvas's right margin.
//
// Tank sizing:
//   • If circumference ≥ height → tank width = 75 % of canvas width,
//     tank height scales to preserve the tank's real aspect.
//   • Otherwise               → tank height = 75 % of canvas height,
//     tank width scales the same way.
//
// No interpolation: empty (θ, Z) cells stay black.
function UnrolledShellCanvas({
  slabbedByBin, binsPerCircle,
  nominalRMm, nominalHMm,
  cMin, cMax, zStepMm,
  histogram, angles, muted,
  canvasRef: externalCanvasRef,
}) {
  const internalCanvasRef = useRef(null);
  const canvasRef = externalCanvasRef || internalCanvasRef;
  const CANVAS_W = C2M_CANVAS.WIDTH;
  const CANVAS_H = C2M_CANVAS.HEIGHT;
  const legendAreaX = C2M_LEGEND.AREA_X;
  const legendAreaW = C2M_LEGEND.AREA_W;

  const circMm = 2 * Math.PI * nominalRMm;
  const H = Math.max(1, nominalHMm);

  // The canvas is split into two sections: tank section (left, from
  // x=0 to where the legend starts) and legend section (right). The
  // tank — and every tank-related glyph (cells + scanline markers) —
  // is sized and centred relative to the tank section, not the full
  // canvas. The 75 %-of-image rule now applies to the tank section.
  const tankSectionW = legendAreaX;        // 2993 px for 3306-wide canvas
  // Fit the unrolled tank inside 75% of BOTH the tank-section width and the
  // canvas height, preserving the circumference:height aspect. A single
  // min-scale is required because the tank section is NOT square
  // (2993 × 1800): the old `if (circMm >= H)` branch pinned the width to 75%
  // and then derived a height that could exceed the canvas (e.g. when
  // circumference is only slightly larger than height), pushing the tank to
  // full height and clipping the scanline arrowheads + labels off the top and
  // bottom edges. Capping both axes leaves ≥12.5% margin for the arrows.
  const fitScale = Math.min(
    (0.75 * tankSectionW) / circMm,
    (0.75 * CANVAS_H) / H,
  );
  const tankW = Math.round(circMm * fitScale);
  const tankH = Math.round(H * fitScale);
  const tankX = Math.round((tankSectionW - tankW) / 2);
  const tankY = Math.round((CANVAS_H   - tankH) / 2);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Pure black background everywhere.
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // ── Tank cells ──────────────────────────────────────────────
    const stripWidthExact = tankW / binsPerCircle;
    const stripDrawW = Math.max(1, Math.ceil(stripWidthExact) + 1);
    const numSlabs   = Math.max(1, Math.round(H / zStepMm) + 1);
    const cellH      = tankH / numSlabs;
    const cellDrawH  = Math.max(1, Math.ceil(cellH) + 1);
    for (const [bin, pts] of slabbedByBin) {
      if (!pts || pts.length === 0) continue;
      const xLeft = tankX + bin * stripWidthExact;
      for (const p of pts) {
        ctx.fillStyle = colorForC2M(p.r - nominalRMm, cMin, cMax);
        const yCenter = tankY + tankH - (p.z / H) * tankH;
        ctx.fillRect(
          Math.floor(xLeft),
          Math.round(yCenter - cellH / 2),
          stripDrawW,
          cellDrawH,
        );
      }
    }

    // ── Scanline markers ────────────────────────────────────────
    // Vertical white double-arrow lines that extend past the tank's
    // top and bottom edges. Drawn via the shared `drawScanlineArrow`
    // helper. Any scanline at θ = 0° (the unwrap seam) is mirrored at
    // the right edge of the tank, since both edges share the same
    // angular position.
    if (Array.isArray(angles) && angles.length > 0 && tankW > 0 && tankH > 0) {
      const ext = C2M_ARROW.EXTENSION;
      for (let i = 0; i < angles.length; i++) {
        const angle = angles[i];
        if (!Number.isFinite(angle)) continue;
        if (muted && muted.has(i)) continue;   // switched off → no arrow
        const label = `SL${i + 1} (${angle}°)`;
        const xMain = tankX + (angle / 360) * tankW;
        drawScanlineArrow(ctx, {
          x1: xMain, y1: tankY - ext,
          x2: xMain, y2: tankY + tankH + ext,
          label, headEnds: 'both', labelAt: 'start',
        });
        if (angle === 0) {
          drawScanlineArrow(ctx, {
            x1: tankX + tankW, y1: tankY - ext,
            x2: tankX + tankW, y2: tankY + tankH + ext,
            label, headEnds: 'both', labelAt: 'start',
          });
        }
      }
    }

    // ── Legend block on the right edge (shared helper) ──────────
    drawC2MLegend(ctx, { cMin, cMax, histogram });
  }, [slabbedByBin, binsPerCircle, nominalRMm, nominalHMm,
      cMin, cMax, zStepMm, tankW, tankH, tankX, tankY, H,
      legendAreaX, legendAreaW, histogram, angles, muted]);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_W}
      height={CANVAS_H}
      className="defo-unrolled-canvas"
    />
  );
}

function ScanlineChart({
  scanlineNumber, angle, slabbedByBin, binsPerCircle, thetaBinDeg, zStepMm,
  // Shared axes across every chart in the grid so they're directly
  // visually comparable. The page memoises these once across all
  // configured scanlines.
  globalR, globalZ, nominalR,
}) {
  // Match the export PNG aspect (800 × 2000 = 1 : 2.5 portrait). The
  // chart's intrinsic viewBox uses small "user-space" numbers so the
  // font sizes / stroke widths stay small and readable; the SVG
  // scales to whatever pixel size the CSS container gives it (and
  // the export rasteriser draws at 800 × 2000). Keeping these two
  // aspect ratios identical kills the white-band issue.
  const W = 320, H = 800;
  const margins = { left: 58, right: 14, top: 22, bottom: 60 };
  const innerW = W - margins.left - margins.right;
  const innerH = H - margins.top - margins.bottom;

  // Snap the configured scanline angle to its nearest θ-bin so we pick
  // up the points the worker accumulated for that ring.
  const binIdx = (() => {
    if (!Number.isFinite(angle)) return -1;
    const raw = Math.round(angle / thetaBinDeg) % binsPerCircle;
    return ((raw % binsPerCircle) + binsPerCircle) % binsPerCircle;
  })();
  const snappedAngle = binIdx >= 0 ? binIdx * thetaBinDeg : null;
  // slabbedByBin is already Z-slabbed upstream (in the page), so we
  // just read the bin's array directly — no further aggregation.
  const pts = (binIdx >= 0 ? slabbedByBin.get(binIdx) : null) || [];

  // Use shared axes if provided; otherwise fall back to the bin's
  // own extent (kept for the empty-grid case).
  const rMin = globalR ? globalR[0] : (pts.length ? Math.min(...pts.map((p) => p.r)) : 0);
  const rMax = globalR ? globalR[1] : (pts.length ? Math.max(...pts.map((p) => p.r)) : 1);
  const zMin = globalZ ? globalZ[0] : (pts.length ? Math.min(...pts.map((p) => p.z)) : 0);
  const zMax = globalZ ? globalZ[1] : (pts.length ? Math.max(...pts.map((p) => p.z)) : 1);

  if (pts.length === 0) {
    return (
      <div className="defo-chart" data-scanline={scanlineNumber}>
        <div className="defo-chart-title">Scanline {scanlineNumber} ({angle}°)</div>
        <div className="defo-chart-empty">No points in this θ-bin.</div>
      </div>
    );
  }

  const xScale = (r) => margins.left + ((r - rMin) / (rMax - rMin)) * innerW;
  const yScale = (z) => margins.top + innerH - ((z - zMin) / (zMax - zMin)) * innerH;

  const rTicks = niceTicks(rMin, rMax, 5);
  // Portrait chart has a tall inner area — more vertical ticks read
  // cleaner than 6.
  const zTicks = niceTicks(zMin, zMax, 12);

  // Polyline points string. Pts already sorted by Z ascending.
  let polyStr = '';
  for (const p of pts) polyStr += `${xScale(p.r).toFixed(1)},${yScale(p.z).toFixed(1)} `;

  return (
    <div className="defo-chart" data-scanline={scanlineNumber}>
      <div className="defo-chart-title">
        Scanline {scanlineNumber} ({angle}°)
        {snappedAngle !== angle && (
          <span className="defo-chart-snap"> (snap {snappedAngle}°)</span>
        )}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="defo-chart-svg" preserveAspectRatio="xMidYMid meet">
        {/* axes */}
        <line
          x1={margins.left} y1={margins.top}
          x2={margins.left} y2={margins.top + innerH}
          className="defo-axis"
        />
        <line
          x1={margins.left} y1={margins.top + innerH}
          x2={margins.left + innerW} y2={margins.top + innerH}
          className="defo-axis"
        />

        {/* X (R) ticks */}
        {rTicks.map((t, i) => (
          <g key={`xt${i}`}>
            <line
              x1={xScale(t)} y1={margins.top + innerH}
              x2={xScale(t)} y2={margins.top + innerH + 4}
              className="defo-axis"
            />
            <text
              x={xScale(t)} y={margins.top + innerH + 16}
              textAnchor="middle" className="defo-tick-label"
            >{Math.round(t)}</text>
          </g>
        ))}

        {/* Y (Z) ticks */}
        {zTicks.map((t, i) => (
          <g key={`yt${i}`}>
            <line
              x1={margins.left - 4} y1={yScale(t)}
              x2={margins.left} y2={yScale(t)}
              className="defo-axis"
            />
            <text
              x={margins.left - 6} y={yScale(t) + 3}
              textAnchor="end" className="defo-tick-label"
            >{Math.round(t)}</text>
          </g>
        ))}

        {/* Axis labels */}
        <text
          x={margins.left + innerW / 2} y={margins.top + innerH + 34}
          textAnchor="middle" className="defo-axis-label"
        >R (mm)</text>
        <text
          x={14} y={margins.top + innerH / 2}
          textAnchor="middle" className="defo-axis-label"
          transform={`rotate(-90, 14, ${margins.top + innerH / 2})`}
        >Z (mm)</text>

        {/* Nominal-radius reference: vertical dashed line at R = R_nominal. */}
        {Number.isFinite(nominalR) && nominalR >= rMin && nominalR <= rMax && (
          <line
            x1={xScale(nominalR)} y1={margins.top}
            x2={xScale(nominalR)} y2={margins.top + innerH}
            stroke="#dc2626"
            strokeWidth="1.2"
            strokeDasharray="4 3"
            fill="none"
          />
        )}

        {/* The shell profile — single polyline, no point markers. */}
        <polyline points={polyStr} className="defo-shell-line" />

        {/* Legend — below the X-axis label, two coloured strokes
            side by side. Sized in user-space units so it scales
            with the SVG and ends up in the PNG export. */}
        <g transform={`translate(0, ${margins.top + innerH + 48})`}>
          {/* Deformed (blue solid) */}
          <line x1={margins.left} y1="6" x2={margins.left + 18} y2="6" stroke="#2563eb" strokeWidth="2" />
          <text x={margins.left + 22} y="9" style={{ fontSize: 9, fill: 'var(--text)' }}>
            Deformed
          </text>
          {/* Nominal (red dashed) */}
          <line x1={margins.left + innerW / 2 + 4} y1="6" x2={margins.left + innerW / 2 + 22} y2="6" stroke="#dc2626" strokeWidth="1.4" strokeDasharray="4 3" />
          <text x={margins.left + innerW / 2 + 26} y="9" style={{ fontSize: 9, fill: 'var(--text)' }}>
            Nominal
          </text>
        </g>
      </svg>
    </div>
  );
}

// ── Angle helpers ───────────────────────────────────────────────────
// Default angles are equally spaced around the tank. The user may then
// override each one within ±360/(2N) of its default, integer-rounded
// (ceil on the lower bound, floor on the upper).

// Site rule for scanline count from nominal diameter:
//   n = round(D / 3.05), forced even, never below 8, and bumped up
//   (in steps of 2) until the chord spacing between adjacent scanlines
//   on the tank circumference is no greater than 10 m.
function autoScanlineCount(diameterMm) {
  if (!diameterMm || diameterMm <= 0) return 8;
  const D_m = diameterMm / 1000;
  let n = Math.round(D_m / 3.05);
  if (n % 2 !== 0) n += 1;
  if (n < 8) n = 8;
  while ((Math.PI * D_m / n) > 10) n += 2;
  return n;
}

function defaultAnglesFor(n) {
  if (n < 1) return [];
  const step = 360 / n;
  return Array.from({ length: n }, (_, i) => ((Math.round(i * step) % 360) + 360) % 360);
}

function angleBoundsFor(idx, n) {
  const step = 360 / n;
  const half = step / 2;
  const center = idx * step;
  const lowerRaw = center - half;
  const upperRaw = center + half;
  // Integer-only inputs: tighten both sides.
  const lower = Math.ceil(lowerRaw);
  const upper = Math.floor(upperRaw);
  const lowerMod = ((lower % 360) + 360) % 360;
  const upperMod = ((upper % 360) + 360) % 360;
  const wraps = lower < 0 || upper > 359 || lowerMod > upperMod;
  return { lower: lowerMod, upper: upperMod, wraps };
}

function isValidAngle(val, idx, n) {
  if (!Number.isInteger(val)) return false;
  if (val < 0 || val > 359) return false;
  const { lower, upper, wraps } = angleBoundsFor(idx, n);
  return wraps ? (val >= lower || val <= upper) : (val >= lower && val <= upper);
}

// ── Page ────────────────────────────────────────────────────────────

export default function ShellDeformationPage() {
  const { id: clientId, objectId, inspectionId } = useParams();
  const { setBreadcrumb, clearBreadcrumb } = useBreadcrumb();
  const { setSaveHandler } = useAnalysisActions();
  // Refs used by the Save-and-close bundle: profile-chart grid (SVG
  // children get rasterised) and the C2M canvas (toDataURL'd in
  // place).
  const chartGridRef = useRef(null);
  const c2mCanvasRef = useRef(null);
  // Mount marker — if the popup tab "doesn't open", check the console
  // for this line. If it's missing, the route never reached this page
  // (auth gate, popup blocker, bundle cache). If it's present, the page
  // mounted and any further issue is a render error after this point.
  // eslint-disable-next-line no-console
  useEffect(() => { console.log('[ShellDeformationPage] mounted'); }, []);

  // Fetch the parent client / object / inspection so we can put their
  // names in the breadcrumb instead of raw IDs. Once loaded, the
  // breadcrumb becomes "Client > Object Tag > Inspection Type > Shell
  // Deformation Analysis"; before they're loaded, fall back to ID.
  const [breadcrumbCtx, setBreadcrumbCtx] = useState({ client: null, object: null, inspection: null });
  // Only render the breadcrumb once the lookups resolve — otherwise the first
  // paint shows raw UUID fallbacks that blink to the real names a moment later.
  const [breadcrumbResolved, setBreadcrumbResolved] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, o, i] = await Promise.all([
          getClientById(clientId),
          getObjectById(objectId),
          getInspectionById(inspectionId),
        ]);
        if (!cancelled) setBreadcrumbCtx({ client: c, object: o, inspection: i });
      } catch {
        if (!cancelled) setBreadcrumbCtx({ client: null, object: null, inspection: null });
      } finally {
        if (!cancelled) setBreadcrumbResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, objectId, inspectionId]);

  useEffect(() => {
    if (!breadcrumbResolved) return undefined;
    const { client, object, inspection } = breadcrumbCtx;
    const crumbs = [];
    crumbs.push({ label: client?.name      || `Client ${clientId}`,         path: null });
    crumbs.push({ label: object?.objectTag || `Object ${objectId}`,         path: null });
    crumbs.push({ label: inspection?.type  || `Inspection ${inspectionId}`, path: null });
    crumbs.push({ label: 'EEMUA 159 Shell Deformation Analysis', path: null, bold: true });
    setBreadcrumb(crumbs);
    return () => clearBreadcrumb();
  }, [breadcrumbResolved, breadcrumbCtx, clientId, objectId, inspectionId, setBreadcrumb, clearBreadcrumb]);

  // EEMUA 159 is the finalized standard for this analysis. No standard
  // selector / sidebar — the page is dedicated to EEMUA 159.
  const [standard] = useState('eemua159');

  // Configuration — the Tank-geometry panel is gone; these defaults
  // are baked in. Scanline count is still state because it's auto-
  // derived from the worker result. Angles remain editable per
  // scanline within their ±360/(2N) bounds.
  const thetaBin    = 1;     // θ-bin size in degrees
  const zRenderStep = 100;   // mm — scanline charts + OOV
  const zImageStep  = 1;     // mm — unrolled image + histogram
  const [scanlineCount, setScanlineCount] = useState(8);
  const [angles,        setAngles]        = useState(() => defaultAnglesFor(8));
  const [file,          setFile]          = useState(null);


  // Scanlines switched off with the power button (0-based indexes into
  // `angles`). A muted scanline is "not computed": its OOV row collapses to
  // one message cell, its profile isn't plotted, and its heatmap arrow isn't
  // drawn. Reset whenever the scanline grid is rebuilt.
  const [muted, setMuted] = useState(() => new Set());

  // Reset scanline angles (and mutes) whenever the count changes.
  useEffect(() => {
    setAngles(defaultAnglesFor(scanlineCount));
    setMuted(new Set());
  }, [scanlineCount]);

  // Run state declared after this block — we touch `result` further down.
  // (See the auto-scanline-count effect below.)

  // Run state.
  const [status,       setStatus]       = useState('idle'); // idle | running | done | error
  const [progressMsgs, setProgressMsgs] = useState([]);
  const [progress,     setProgress]     = useState({ stage: '', pct: 0 });
  const [result,       setResult]       = useState(null);
  const [error,        setError]        = useState('');
  const workerRef = useRef(null);

  // Brief "recomputing" flash shown by the progress modal whenever a
  // scanline angle changes. Charts re-derive from existing memos, so
  // the recompute is effectively instant — we show the modal for a
  // tick so the user gets visual confirmation that the change landed.
  const [recompute, setRecompute] = useState(false);
  const recomputeTimerRef = useRef(null);
  const flashRecompute = () => {
    if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
    setRecompute(true);
    recomputeTimerRef.current = setTimeout(() => setRecompute(false), 350);
  };

  // Register the Save-and-close handler with the AnalysisLayout
  // whenever there's a result to save (and we're on the EEMUA tab —
  // API 653 has nothing to ship yet). The layout's Save button
  // awaits this before window.close().

  // Auto-derive scanline count once the worker reports a nominal D.
  // The effect on `scanlineCount` above then resets angles to the
  // equally-spaced defaults for the new count.
  useEffect(() => {
    if (result?.nominal?.D_mm) {
      setScanlineCount(autoScanlineCount(result.nominal.D_mm));
    }
  }, [result]);

  function appendMsg(msg) {
    setProgressMsgs((prev) => [...prev, { t: Date.now(), msg }]);
  }

  // Clear every piece of run-state that's tied to the previously-loaded
  // file. Called when the user picks a new file (even one with the same
  // name) so we never end up with a half-detached ArrayBuffer or a
  // still-running worker holding the old data.
  function resetForNewFile() {
    if (workerRef.current) {
      try { workerRef.current.terminate(); } catch (e) { /* noop */ }
      workerRef.current = null;
    }
    setResult(null);
    setError('');
    setProgressMsgs([]);
    setProgress({ stage: '', pct: 0 });
    setStatus('idle');
    if (typeof window !== 'undefined') window.__defoResult = null;
  }

  function setAngle(idx, val) {
    setAngles((arr) => arr.map((a, i) => (i === idx ? val : a)));
  }

  // ── Download coloured 3D point cloud (.txt ASCII) ──────────────────
  // Re-parses the original file and emits every point at its actual 3D
  // position (tank axis at origin, base at Z=0), coloured by its C2M cell.
  const [exporting, setExporting] = useState(false);
  async function handleDownloadModel() {
    if (exporting || !result || !c2mStats || !file) return;
    setExporting(true);
    try {
      // Colour every C2M cell once, with the same gradient the heatmap uses,
      // keyed exactly as the worker keys points: zMm * binsPerCircle + binIdx.
      const cells = new Float32Array(result.cellsBuffer);
      const binsPerCircle = result.binsPerCircle;
      const nominalRMm = result.nominal.R_mm;
      const { cMin, cMax } = c2mStats;
      const range = cMax - cMin;
      const colorMap = new Map();
      for (let i = 0; i < cells.length; i += 4) {
        const theta = cells[i];
        const zMm   = cells[i + 1];
        const r     = cells[i + 2];
        let binIdx = Math.round(theta / thetaBin) % binsPerCircle;
        if (binIdx < 0) binIdx += binsPerCircle;
        const key = zMm * binsPerCircle + binIdx;
        const pct = range > 0 ? ((r - nominalRMm - cMin) / range) * 100 : 50;
        const [rr, gg, bb] = rgbAtPercent(pct);
        colorMap.set(key, (rr << 16) | (gg << 8) | bb);
      }

      const buffer = await file.arrayBuffer();
      const worker = new Worker(new URL('../workers/pointCloudExport.worker.js', import.meta.url));
      worker.onmessage = (ev) => {
        const m = ev.data;
        if (m.kind === 'done') {
          const tag = breadcrumbCtx?.object?.objectTag || 'model';
          const blob = new Blob([m.text], { type: 'text/plain' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${tag}_Shell_C2M.txt`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
          worker.terminate();
          setExporting(false);
        } else if (m.kind === 'error') {
          // eslint-disable-next-line no-alert
          alert('Export failed: ' + m.message);
          worker.terminate();
          setExporting(false);
        }
      };
      worker.onerror = (e) => {
        // eslint-disable-next-line no-alert
        alert('Export failed: ' + (e?.message || 'worker error'));
        worker.terminate();
        setExporting(false);
      };
      worker.postMessage({
        mode: 'shell',
        buffer,
        colorMap,
        cx: result.center.cx,
        cy: result.center.cy,
        zRef: result.center.zRef,
        thetaBinDeg: thetaBin,
        binsPerCircle,
      }, [buffer]);
    } catch (e) {
      setExporting(false);
      // eslint-disable-next-line no-alert
      alert('Export failed: ' + (e?.message || e));
    }
  }

  const allAnglesValid = useMemo(
    () => angles.every((a, i) => isValidAngle(a, i, scanlineCount)),
    [angles, scanlineCount],
  );

  // Z-slabbed cylindrical cloud, indexed by θ-bin. Re-aggregates the
  // raw worker output (1 mm Z resolution) into uniform Z-slabs.
  // Two independent slabbings now exist so the scanline charts (coarser,
  // typically 100 mm) and the unrolled image / histogram (finer,
  // typically 10 mm) can be tuned independently.
  const buildSlabbed = (slabMm) => {
    const out = new Map();
    if (!result || !result.cellsBuffer) return out;
    const cells = new Float32Array(result.cellsBuffer);
    const { binsPerCircle, thetaBinDeg } = result;
    const slab = Math.max(1, slabMm);
    const acc = new Map();
    for (let i = 0; i < cells.length; i += 4) {
      const theta = cells[i];
      const z     = cells[i + 1];
      const r     = cells[i + 2];
      const bin   = (((Math.round(theta / thetaBinDeg) % binsPerCircle) + binsPerCircle) % binsPerCircle);
      let slabMap = acc.get(bin);
      if (!slabMap) { slabMap = new Map(); acc.set(bin, slabMap); }
      const slabZ = Math.round(z / slab) * slab;
      const e = slabMap.get(slabZ);
      if (e) { e.sumR += r; e.count++; }
      else   { slabMap.set(slabZ, { sumR: r, count: 1 }); }
    }
    for (const [bin, slabMap] of acc) {
      const arr = [];
      for (const [z, e] of slabMap) arr.push({ z, r: e.sumR / e.count });
      arr.sort((a, b) => a.z - b.z);
      out.set(bin, arr);
    }
    return out;
  };

  // Scanline charts + OOV table use the (coarser) chart Z step.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const slabbedByBin = useMemo(() => buildSlabbed(zRenderStep), [result, zRenderStep]);
  // Unrolled image + histogram use the (finer) image Z step.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const imageSlabbedByBin = useMemo(() => buildSlabbed(zImageStep), [result, zImageStep]);

  // Global R/Z range across every configured scanline so the chart
  // grid uses one consistent set of axes. Nominal R is always
  // included in the R range so the dashed reference line is on-canvas
  // even when the deformed profile sits entirely to one side of it.
  const scanlineGlobalRange = useMemo(() => {
    if (!result || !slabbedByBin || slabbedByBin.size === 0) {
      return { r: null, z: null };
    }
    let rMin = Infinity, rMax = -Infinity, zMin = Infinity, zMax = -Infinity;
    const { binsPerCircle, thetaBinDeg } = result;
    for (const [i, a] of angles.entries()) {
      if (!Number.isFinite(a) || muted.has(i)) continue;   // muted → not computed
      const binIdx = ((Math.round(a / thetaBinDeg) % binsPerCircle) + binsPerCircle) % binsPerCircle;
      const pts = slabbedByBin.get(binIdx);
      if (!pts) continue;
      for (const p of pts) {
        if (p.r < rMin) rMin = p.r;
        if (p.r > rMax) rMax = p.r;
        if (p.z < zMin) zMin = p.z;
        if (p.z > zMax) zMax = p.z;
      }
    }
    if (!Number.isFinite(rMin)) return { r: null, z: null };
    // Include nominal R in the R-range so the dashed reference line
    // always lands on-canvas.
    const nR = result.nominal.R_mm;
    if (nR < rMin) rMin = nR;
    if (nR > rMax) rMax = nR;
    const rPad = (rMax - rMin) * 0.04 || 1;
    const zPad = (zMax - zMin) * 0.02 || 1;
    return {
      r: [rMin - rPad, rMax + rPad],
      z: [zMin - zPad, zMax + zPad],
    };
  }, [result, slabbedByBin, angles, muted]);

  // Range stats of the image-slabbed C2M differences. The histogram is
  // the legend for the unrolled image, so both use the same (image)
  // slabbed cloud to guarantee identical colour mappings.
  const c2mStats = useMemo(() => {
    if (!result || imageSlabbedByBin.size === 0) return null;
    const nominalRMm = result.nominal.R_mm;
    let cMin = Infinity, cMax = -Infinity, n = 0;
    for (const arr of imageSlabbedByBin.values()) {
      for (const p of arr) {
        const d = p.r - nominalRMm;
        if (d < cMin) cMin = d;
        if (d > cMax) cMax = d;
        n++;
      }
    }
    if (!Number.isFinite(cMin)) return null;
    return { cMin, cMax, range: cMax - cMin, totalCells: n };
  }, [result, imageSlabbedByBin]);

  // Min-max histogram (200 bins) over the image-slabbed cloud.
  // The raw counts are then smoothed with a box filter of radius 3
  // (window = 7 bins) so the line-graph fill renders as a continuous
  // envelope instead of a comb — over-fine bins on discrete data
  // produce zero gaps between populated bins, which the box filter
  // bridges by averaging each bin with its neighbours.
  const histogramData = useMemo(() => {
    if (!result || !c2mStats || c2mStats.range <= 0) return null;
    const nominalRMm = result.nominal.R_mm;
    const NBINS = 200;
    const SMOOTH_RADIUS = 3;
    const { cMin, cMax, range } = c2mStats;
    const binWidth = range / NBINS;

    const rawCounts = new Int32Array(NBINS);
    for (const arr of imageSlabbedByBin.values()) {
      for (const p of arr) {
        const d = p.r - nominalRMm;
        let idx = Math.floor((d - cMin) / binWidth);
        if (idx >= NBINS) idx = NBINS - 1;
        if (idx < 0) idx = 0;
        rawCounts[idx]++;
      }
    }

    // Box filter — windowed mean across ±SMOOTH_RADIUS bins. Stored as
    // Float32 because the result is no longer integer-valued.
    const counts = new Float32Array(NBINS);
    for (let i = 0; i < NBINS; i++) {
      let sum = 0, n = 0;
      const lo = Math.max(0, i - SMOOTH_RADIUS);
      const hi = Math.min(NBINS - 1, i + SMOOTH_RADIUS);
      for (let j = lo; j <= hi; j++) { sum += rawCounts[j]; n++; }
      counts[i] = sum / n;
    }

    let maxCount = 0;
    for (let i = 0; i < NBINS; i++) if (counts[i] > maxCount) maxCount = counts[i];
    return { counts, binWidth, cMin, cMax, maxCount, NBINS, totalCells: c2mStats.totalCells };
  }, [result, c2mStats, imageSlabbedByBin]);

  // OOV per configured scanline: R at the topmost Z minus R at the
  // bottommost Z of the bin closest to the scanline's angle. Allowable
  // OOV is 1 % of the nominal height; "% of allowable" reports the
  // absolute OOV magnitude against that limit.
  const oovRows = useMemo(() => {
    if (!result) return [];
    const Hmm = result.nominal.H_mm;
    const allowMm = Hmm * 0.01;
    return angles.map((a, i) => {
      const raw = ((Math.round(a / result.thetaBinDeg) % result.binsPerCircle)
                   + result.binsPerCircle) % result.binsPerCircle;
      if (muted.has(i)) {
        return { scanline: i + 1, angle: a, snapped: raw * result.thetaBinDeg, muted: true,
                 rBottom: null, rTop: null, oov: null, allow: allowMm, pct: null };
      }
      const pts = slabbedByBin.get(raw) || [];
      if (pts.length < 2) {
        return { scanline: i + 1, angle: a, snapped: raw * result.thetaBinDeg,
                 rBottom: null, rTop: null, oov: null, allow: allowMm, pct: null };
      }
      const rBottom = pts[0].r;
      const rTop    = pts[pts.length - 1].r;
      const oov     = rTop - rBottom;
      const pct     = allowMm > 0 ? (Math.abs(oov) / allowMm) * 100 : 0;
      return { scanline: i + 1, angle: a, snapped: raw * result.thetaBinDeg,
               rBottom, rTop, oov, allow: allowMm, pct };
    });
  }, [result, angles, slabbedByBin, muted]);

  // Power button: switch one scanline off/on. The rebuild flash blocks the
  // page (and these buttons are disabled) until outputs have re-rendered, so
  // a second toggle can only start after the first has finished.
  function toggleMuted(idx) {
    if (recompute || status === 'running') return;
    setMuted((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
    flashRecompute();
  }

  async function handleStart() {
    if (!file) { setError('Please select a point cloud TXT file first.'); return; }
    if (!allAnglesValid) { setError('One or more scanline angles are outside their valid range.'); return; }
    // Belt-and-braces: kill any worker that's still around from a
    // previous run before we spin a new one up. Avoids two workers
    // contending for the same transferred buffer.
    if (workerRef.current) {
      try { workerRef.current.terminate(); } catch (e) { /* noop */ }
      workerRef.current = null;
    }
    setError('');
    setProgressMsgs([]);
    setResult(null);
    setProgress({ stage: '', pct: 0 });
    setStatus('running');
    appendMsg(`Reading "${file.name}" (${(file.size / (1024 * 1024)).toFixed(2)} MB) into memory…`);

    let buffer;
    try {
      buffer = await file.arrayBuffer();
    } catch (e) {
      setStatus('error');
      setError('Failed to read file: ' + e.message);
      return;
    }
    appendMsg(`File loaded. Spawning Web Worker…`);

    const worker = new Worker(new URL('../workers/shellDeformation.worker.js', import.meta.url));
    workerRef.current = worker;
    worker.onmessage = (ev) => {
      const m = ev.data;
      if (m.kind === 'stage') {
        appendMsg(m.message || m.stage);
        setProgress({ stage: m.stage, pct: 0 });
      } else if (m.kind === 'progress') {
        if (m.stage === 'parse') {
          // Parser reports lines/points directly — turn it into pct of a
          // hypothetical (unknown) total based on cumulative bytes/lines.
          setProgress({ stage: 'parse', pct: 0, label: `parsed ${m.points.toLocaleString()} pts / ${m.lines.toLocaleString()} lines` });
        } else if (m.total > 0) {
          const pct = Math.min(100, Math.round((m.done / m.total) * 100));
          setProgress({ stage: m.stage, pct });
        }
      } else if (m.kind === 'done') {
        // Expose the buffer on window for ad-hoc inspection from
        // DevTools — useful while building out subsequent stages.
        window.__defoResult = m;
        appendMsg(`Total worker time: ${(m.durationMs / 1000).toFixed(2)} s.`);
        appendMsg('Done. (Cylindrical point cloud held in window.__defoResult — output rendering is the next milestone.)');
        // Pre-compute scanlineCount + default angles before flipping
        // status off 'running' so the progress modal closes onto the
        // FINAL scanline grid (no 8-default → real-count flash).
        const finalCount = autoScanlineCount(m.nominal?.D_mm);
        setResult(m);
        setScanlineCount(finalCount);
        setAngles(defaultAnglesFor(finalCount));
        setMuted(new Set());   // same count as last run → the count effect won't reset it
        setStatus('done');
        setProgress({ stage: 'done', pct: 100 });
      } else if (m.kind === 'error') {
        appendMsg('Error: ' + m.message);
        setError(m.message);
        setStatus('error');
      }
    };
    worker.onerror = (e) => {
      setError('Worker error: ' + (e.message || 'unknown'));
      setStatus('error');
    };
    // Transfer the buffer (zero-copy) — the main-thread reference is detached.
    worker.postMessage({ buffer, thetaBinDeg: thetaBin }, [buffer]);
  }

  function handleAbort() {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    setStatus('idle');
    appendMsg('Aborted by user.');
  }

  // Bin maths shown to the user as a sanity check.
  const halfBin = (scanlineCount > 0) ? (360 / (2 * scanlineCount)) : 0;

  // ── Save-and-close handler ──────────────────────────────────
  useEffect(() => {
    if (!result || standard !== 'eemua159' || !inspectionId) {
      setSaveHandler(null);
      return undefined;
    }
    setSaveHandler(async () => {
      const { payload, images } = await bundleShellResults({
        result,
        angles,
        oovRows,
        mutedScanlines: [...muted].sort((a, b) => a - b).map((i) => i + 1),
        chartGridEl: chartGridRef.current,
        c2mCanvasEl: c2mCanvasRef.current,
      });
      await post(`/inspections/${inspectionId}/defo-results`, {
        kind: 'shell',
        standard: 'eemua159',
        payload,
        images,
      });
    });
    return () => setSaveHandler(null);
  }, [result, standard, angles, oovRows, muted, inspectionId, setSaveHandler]);

  return (
    <div className="defo-page">
      <header className="defo-header">
        <h1>Shell Deformation Analysis</h1>
      </header>

      {/* ── Control card — heading reflects active standard, contains
            disclaimer + file picker + Run button (or placeholder for
            API 653). One canonical input control for the page. ─── */}
      <section className="defo-card defo-control-card">
        <div className="defo-standard-card-header">
          <h2 className="insp-info-title">EEMUA 159</h2>
        </div>
        <p className="defo-sub defo-upload-disclaimer">
          All processing runs locally in this browser tab — no data is sent
          to the server. <strong>Nothing is saved until you click Save and close.</strong>{' '}
          All results live in this tab; closing the tab without saving loses them.
        </p>
        <div className="defo-upload-row">
          <label className="defo-config-label">Point cloud ASCII file</label>
          <div className="defo-file-picker">
            <input
              id="defo-shell-file-input"
              type="file"
              accept=".txt,.asc,.xyz,.csv"
              className="defo-file-input-hidden"
              onClick={(e) => { e.currentTarget.value = ''; }}
              onChange={(e) => {
                resetForNewFile();
                setFile(e.target.files?.[0] || null);
              }}
              disabled={status === 'running'}
            />
            <label
              htmlFor="defo-shell-file-input"
              className={`btn btn-secondary defo-file-btn${status === 'running' ? ' disabled' : ''}`}
            >
              Choose file
            </label>
            <div className={`defo-file-display${file ? ' has-file' : ''}`}>
              <span className="defo-file-name">
                {file
                  ? `${file.name} · ${(file.size / (1024 * 1024)).toFixed(2)} MB`
                  : 'No file chosen'}
              </span>
              {file && (
                <button
                  type="button"
                  className="defo-file-clear"
                  onClick={() => { resetForNewFile(); setFile(null); }}
                  disabled={status === 'running'}
                  aria-label="Remove file"
                  title="Remove file"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="defo-actions">
          <button
            className="btn btn-primary defo-run-btn"
            onClick={handleStart}
            disabled={!file || status === 'running' || !allAnglesValid}
          >
            {status === 'running' ? 'Running…' : 'Run Deformation'}
          </button>
          {status === 'running' && (
            <button className="btn btn-secondary defo-run-btn" onClick={handleAbort}>Abort</button>
          )}
        </div>
      </section>

      {/* ── Results card — only appears once a run completes; the
            progress modal handles the running state. Never shown for
            API 653. ────────────────────────────────────────────── */}
      {standard === 'eemua159' && result && (
      <section className="defo-card defo-results-card">
        <div className="defo-standard-card-header">
          <h2 className="insp-info-title">Results</h2>
        </div>

      {result && (
        <section className="defo-subcard">
          <h2>Scanline angles</h2>
          <p className="defo-card-hint">
            θ = 0° points along the +Y axis and increases clockwise through +X, −Y, −X.
            Scanline 1 defaults to 0°, the rest are equally spaced clockwise around the tank.
            Each angle may be overridden within ±{halfBin.toFixed(2)}° of its default
            (integer values only). Changing an angle re-renders the outputs immediately;
            no rebuild needed.
          </p>
          <div className="defo-angles-grid">
            {angles.map((a, i) => {
              const b = angleBoundsFor(i, scanlineCount);
              const valid = isValidAngle(a, i, scanlineCount);
              const rangeText = b.wraps
                ? `valid: ${b.lower}…359, 0…${b.upper}`
                : `valid: ${b.lower}…${b.upper}`;
              const isOff = muted.has(i);
              return (
                <label key={i} className={`defo-angle ${valid ? '' : 'invalid'}${isOff ? ' muted' : ''}`}>
                  <div className="defo-angle-head">
                    <span>Scanline {i + 1}</span>
                    {/* On (green) shows only on hover; off (red) always shows. */}
                    <button
                      type="button"
                      className={`defo-power-btn ${isOff ? 'off' : 'on'}`}
                      onClick={(e) => { e.preventDefault(); toggleMuted(i); }}
                      disabled={recompute || status === 'running'}
                      aria-pressed={!isOff}
                      title={isOff
                        ? `Scanline ${i + 1} is off (not computed) — click to turn on`
                        : `Turn off scanline ${i + 1} (exclude from the results)`}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                        <line x1="12" y1="2" x2="12" y2="12" />
                      </svg>
                    </button>
                  </div>
                  <div className="defo-angle-input-row">
                    <AngleInput
                      value={a}
                      disabled={status === 'running'}
                      onCommit={(v) => {
                        setAngle(i, v);
                        flashRecompute();
                      }}
                    />
                    <span className="defo-angle-unit">°</span>
                  </div>
                  <small>{rangeText}</small>
                </label>
              );
            })}
          </div>
        </section>
      )}

      {result && (
        <section className="defo-subcard">
          <h2>Nominal geometry</h2>
          <p className="defo-card-hint">
            Mean radius across the whole cloud is computed first; points within
            ±σ of it (i.e. <code>(Rᵢ − R̄)² ≤ σ²</code>) form the "nominal pool",
            and the nominal radius is the mean of that filtered set. Nominal
            height is simply the Z-extent.
          </p>
          <table className="defo-stats-table">
            <tbody>
              <tr><th>Points parsed and read</th><td>{result.pointCount.toLocaleString()}</td></tr>
              <tr><th>X bounds (mm)</th><td>{Math.round(result.bounds.minX * 1000).toLocaleString()} → {Math.round(result.bounds.maxX * 1000).toLocaleString()}</td></tr>
              <tr><th>Y bounds (mm)</th><td>{Math.round(result.bounds.minY * 1000).toLocaleString()} → {Math.round(result.bounds.maxY * 1000).toLocaleString()}</td></tr>
              <tr><th>Z bounds (mm)</th><td>{Math.round(result.bounds.minZ * 1000).toLocaleString()} → {Math.round(result.bounds.maxZ * 1000).toLocaleString()}</td></tr>
              <tr><th>Mean radius (raw R̄)</th><td>{result.nominal.meanR_mm.toLocaleString()} mm</td></tr>
              <tr><th>θ bins</th><td>{result.binsPerCircle} × {result.thetaBinDeg}°</td></tr>
              <tr><th>σ (stdev of R)</th><td>{Math.round(result.nominal.stdev_mm).toLocaleString()} mm</td></tr>
              <tr>
                <th>Nominal pool</th>
                <td>
                  {result.nominal.nominalPoolCount.toLocaleString()} / {result.nominal.totalCount.toLocaleString()}
                  {' '}({(100 * result.nominal.nominalPoolCount / result.nominal.totalCount).toFixed(1)} %)
                </td>
              </tr>
              <tr><th>Nominal radius (R)</th><td>{result.nominal.R_mm.toLocaleString()} mm</td></tr>
              <tr><th>Nominal diameter (D = 2R)</th><td>{result.nominal.D_mm.toLocaleString()} mm</td></tr>
              <tr><th>Nominal height (H)</th><td>{result.nominal.H_mm.toLocaleString()} mm</td></tr>
              <tr><th>Total time</th><td>{(result.durationMs / 1000).toFixed(2)} s</td></tr>
            </tbody>
          </table>
        </section>
      )}

      {result && (
        <section className="defo-subcard">
          <h2>Out of Verticality (OOV)</h2>
          <p className="defo-card-hint">
            Per scanline: OOV = R(Z<sub>max</sub>) − R(Z<sub>min</sub>) using the
            bin closest to the scanline angle. Allowable OOV is 1 % of nominal
            height ({Math.round(result.nominal.H_mm * 0.01).toLocaleString()} mm).
          </p>
          <table className="defo-oov-table">
            <thead>
              <tr>
                <th>Scanline (Angle°)</th>
                <th>R bottom corner-weld (mm)</th>
                <th>R top corner-weld (mm)</th>
                <th>Out of Verticality (mm)</th>
                <th>Allowable limit (mm)</th>
                <th>Percentage deviation from limit (%)</th>
              </tr>
            </thead>
            <tbody>
              {oovRows.map((r) => {
                const cls = r.pct == null
                  ? ''
                  : (r.pct < 80 ? 'ok' : (r.pct <= 90 ? 'warn' : 'fail'));
                const label = (
                  <td>
                    {r.scanline} ({r.angle}°)
                    {r.snapped !== r.angle && (
                      <span className="defo-chart-snap"> (snap {r.snapped}°)</span>
                    )}
                  </td>
                );
                if (r.muted) {
                  return (
                    <tr key={r.scanline} className="defo-oov-muted">
                      {label}
                      <td colSpan={5}>Scanline was not computed</td>
                    </tr>
                  );
                }
                return (
                  <tr key={r.scanline}>
                    {label}
                    <td>{r.rBottom != null ? Math.round(r.rBottom).toLocaleString() : '—'}</td>
                    <td>{r.rTop    != null ? Math.round(r.rTop).toLocaleString()    : '—'}</td>
                    <td>{r.oov     != null ? Math.round(r.oov).toLocaleString()     : '—'}</td>
                    <td>{Math.round(r.allow).toLocaleString()}</td>
                    <td className={`defo-oov-pct ${cls}`}>
                      {r.pct != null ? `${r.pct.toFixed(1)} %` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {result && (
        <section className="defo-subcard">
          <h2>Scanline Shell profiles</h2>
          <p className="defo-card-hint">
            One chart per configured scanline. Each chart shows the radius at the
            scanline's θ-bin against height, with R values averaged into Z-slabs
            of {zRenderStep} mm (configured above). Scanline angles that don't
            land on an exact bin centre are snapped to the nearest bin.
          </p>
          <div className="defo-chart-grid" ref={chartGridRef}>
            {angles.map((a, i) => muted.has(i) ? null : (
              <ScanlineChart
                key={i}
                scanlineNumber={i + 1}
                angle={a}
                slabbedByBin={slabbedByBin}
                binsPerCircle={result.binsPerCircle}
                thetaBinDeg={result.thetaBinDeg}
                zStepMm={zRenderStep}
                globalR={scanlineGlobalRange.r}
                globalZ={scanlineGlobalRange.z}
                nominalR={result.nominal.R_mm}
              />
            ))}
          </div>
        </section>
      )}

      {result && c2mStats && (
        <section className="defo-subcard">
          <h2>Unrolled Shell Deformation Heatmap</h2>
          <div className="defo-unrolled-canvas-wrap">
            <UnrolledShellCanvas
              slabbedByBin={imageSlabbedByBin}
              binsPerCircle={result.binsPerCircle}
              nominalRMm={result.nominal.R_mm}
              nominalHMm={result.nominal.H_mm}
              cMin={c2mStats.cMin}
              cMax={c2mStats.cMax}
              zStepMm={zImageStep}
              histogram={histogramData}
              angles={angles}
              muted={muted}
              canvasRef={c2mCanvasRef}
            />
          </div>
        </section>
      )}

      {result && c2mStats && file && (
        <section className="defo-subcard">
          <h2>Download 3D Model (.txt ASCII)</h2>
          <p className="defo-card-hint">
            A full-density coloured point cloud of the scanned shell rolled back
            to 3D (tank axis at origin, base at Z = 0), each point coloured
            exactly by its C2M cell. Tab-delimited X Y Z R G B, coordinates in
            metres to 3 decimal precision and colors ranging from 0 to 255.
            Open the .txt ASCII file in CloudCompare and export as .las file or
            other 3D point cloud formats.
          </p>
          <button
            className="btn btn-primary defo-run-btn"
            onClick={handleDownloadModel}
            disabled={exporting}
          >
            {exporting ? 'Generating…' : 'Download .txt'}
          </button>
        </section>
      )}
      </section>
      )}

      {/* ── Progress modal — blocks the page during the worker run
            and flashes briefly during scanline rebuilds. Cannot be
            dismissed; auto-closes when status flips away from
            running (or the recompute timer expires). ─────────── */}
      <ProgressModal
        open={status === 'running' || recompute}
        title={recompute && status !== 'running' ? 'Rebuilding scanline outputs…' : 'Running deformation…'}
        progress={status === 'running' ? progress : { stage: 'Rebuilding…', pct: 100 }}
        progressMsgs={status === 'running' ? progressMsgs : []}
        error={status === 'running' ? error : ''}
      />
    </div>
  );
}
